import React, { useEffect, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCloudArrowUp,
  faFileExcel,
  faSpinner,
  faCheckCircle,
  faCircle,
  faTriangleExclamation,
  faCalendarDays,
  faRotateLeft,
  faIndustry,
  faLock,
} from '@fortawesome/free-solid-svg-icons';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import { usePrefersReducedMotion } from 'modules/Common/hooks/usePrefersReducedMotion';
import { SelectField } from 'modules/Common/form/v2';
import InfoLabel from './InfoLabel';
import { useCreateSupplierUpload } from 'modules/FreeSupplier/useSupplierUploads';
import {
  SUPPLIER_UPLOAD_MISSING_INSTALLATION_COPY,
  isSupplierUploadMissingInstallationCode,
  supplierUploadRejectionCopy,
  isSupplierUploadPeriodRequiredCode,
  isSupplierUploadWorkbookFixCode,
  SUPPLIER_UPLOAD_PERIOD_ISSUE_FALLBACK,
} from 'modules/FreeSupplier/supplierUploadPresentation';
import TemplateIssuesPanel from 'modules/Common/templateIssues/TemplateIssuesPanel';
import ReportingPeriodPrompt from 'modules/Common/templateIssues/ReportingPeriodPrompt';
import { createUploadPhaseSimulation } from 'modules/FreeSupplier/supplierUploadPhaseProgress';
import {
  UploadQueueItemStatus,
  isUploadQueueReadyToComplete,
  isUploadQueueSettled,
  nextQueueItemToProcess,
  uploadQueueHasSuccess,
  uploadQueueReducer,
  isUploadQueueItemAwaitingPeriod,
} from 'modules/FreeSupplier/supplierUploadQueue';
import { useMockData } from '../state/MockDataContext';

// Drag-drop zone for the S6 upload pipeline: POST /supplier/uploads accepts
// exactly ONE file per request — quarantine → hostile-file boundary → parse
// → validate → draft creation all happen synchronously inside that one
// request/response cycle, and the backend runs a 2-slot parse semaphore. A
// drop/pick of MULTIPLE .xlsx files is therefore queued client-side and
// uploaded SEQUENTIALLY, one request at a time (see supplierUploadQueue.js
// for the queue model) — never in parallel, so this tab never burns more
// than one of the two parse slots, and a busy 3rd concurrent parse (from
// this tab or another) surfaces as an ordinary per-file PARSER_BUSY retry
// rather than blocking the rest of the batch.
//
// Nothing is published here: onComplete(items) fires once the batch is
// fully settled (every file done or failed) AND at least one draft was
// produced, handing back every settled queue item for review — never a
// live factor. A batch that fails outright (zero successes) stays on
// screen so every row's own Retry stays reachable, instead of forcing a
// dead-end "success" transition.
//
// - lockedInstallation (R1, S7 fix batch 3): when the caller has already
//   resolved the exact installation a request-tailored upload must target
//   (RequestUploadPage's `matched` tri-state), the picker is replaced with a
//   read-only context row and `installationId` can never be changed away
//   from it — a multi-site supplier can no longer upload a request-scoped
//   drop against the wrong site. Absent for the plain, non-request
//   `/supplier/upload` flow, which keeps the original free picker. Every
//   file in a batch targets the same installation.

// Sentinel Select value for "detect the installation from the template"
// (S10 backend amendment: `installationId` is now optional on the create
// request — an omitted id tells the backend to infer the installation from
// the template's own extracted entity data). Radix's Select reserves the
// empty string for "no selection" (an item can't use it as a value), and
// this is never a legal Mongo ObjectId, so it can never collide with a real
// installation id.
const DETECT_INSTALLATION_VALUE = '__detect_from_template__';

// Module-scope (not exported/reused elsewhere) — built as a function of `t`,
// called directly (no useMemo — called at most twice per render, from two
// mutually-exclusive branches below).
function buildInstallationOptions(t, installations) {
  return [
    {
      value: DETECT_INSTALLATION_VALUE,
      label: t(
        'freeSupplier:commsTemplateDropzone.detectFromTemplateOption',
        'Detect from template (recommended)',
      ),
    },
    ...installations.map((inst) => ({ value: String(inst._id), label: inst.name })),
  ];
}

// The "brief beat" a just-completed row holds its swept-to-100%/all-phases
// view for before collapsing into the compact "N drafts ready" row — purely
// cosmetic (see the sequential-processing effect below): it never delays
// the SUCCESS dispatch itself, so it can't slow down the next queued file.
const REVEAL_HOLD_MS = 550;

// The 5-named-phase progress readout for a row that is actively uploading
// (or briefly revealing its just-completed sweep — see REVEAL_HOLD_MS).
// Purely presentational: `snapshot` is a createUploadPhaseSimulation()
// emission, `complete` forces the "all done" label instead of the last
// active phase's own name.
function UploadPhaseProgress({ snapshot, complete }) {
  const { t } = useTranslation('freeSupplier');
  if (!snapshot) return null;
  const currentLabel = complete
    ? t('commsTemplateDropzone.allDonePhaseLabel', 'All done')
    : snapshot.phases[snapshot.phaseIndex]?.label || snapshot.phases[0].label;
  return (
    <div className="mt-2 ml-7 flex flex-col gap-1.5">
      <div className="flex items-center gap-1">
        {snapshot.phases.map((phase, index) => (
          <React.Fragment key={phase.key}>
            <span
              aria-hidden="true"
              className={`w-1.5 h-1.5 rounded-full shrink-0 transition-colors motion-reduce:transition-none ${
                phase.complete
                  ? 'bg-primaryColor'
                  : phase.active
                    ? 'bg-primaryColor/50'
                    : 'bg-slate-200'
              }`}
            />
            {index < snapshot.phases.length - 1 && (
              <span
                aria-hidden="true"
                className={`h-px flex-1 ${phase.complete ? 'bg-primaryColor/40' : 'bg-slate-100'}`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
      <p className="text-[11px] text-slate-500 m-0">{currentLabel}</p>
      <div
        role="progressbar"
        aria-label={t('commsTemplateDropzone.uploadProgressAriaLabel', 'Upload progress')}
        aria-valuenow={Math.round(snapshot.percent)}
        aria-valuemin={0}
        aria-valuemax={100}
        className="h-1.5 rounded-full bg-slate-100 overflow-hidden"
      >
        <div
          className="h-full rounded-full bg-primaryColor transition-[width] duration-300 ease-out motion-reduce:transition-none"
          style={{ width: `${snapshot.percent}%` }}
        />
      </div>
    </div>
  );
}

export default function CommsTemplateDropzone({ onComplete, lockedInstallation }) {
  const { t } = useTranslation('freeSupplier');
  const { installationDocs } = useMockData();
  const createUpload = useCreateSupplierUpload();
  const prefersReducedMotion = usePrefersReducedMotion();
  const [isDragging, setIsDragging] = useState(false);
  const [items, dispatch] = useReducer(uploadQueueReducer, []);
  const [installationId, setInstallationId] = useState(
    lockedInstallation ? String(lockedInstallation._id) : DETECT_INSTALLATION_VALUE,
  );
  // The latest phase-progress snapshot per item id (Item B) — keyed rather
  // than a single value so a just-settled item's brief "revealing" hold can
  // coexist with the NEXT item already uploading.
  const [phaseSnapshots, setPhaseSnapshots] = useState({});
  // Item ids currently holding their swept-to-100%/all-complete view before
  // collapsing into the compact "N drafts ready" row (REVEAL_HOLD_MS).
  const [revealingIds, setRevealingIds] = useState(() => new Set());
  const fileInputRef = useRef(null);
  // Guards the sequential processing effect so a single batch item is never
  // started twice (re-renders triggered by the item's own START dispatch,
  // React 18 StrictMode's double mount-effect invocation, etc).
  const processingRef = useRef(false);
  // Guards onComplete so a settled-with-success batch only fires once.
  const firedCompleteRef = useRef(false);
  // Live phase simulations + pending reveal-hold timeouts, keyed by item id
  // — imperative (not state) since they're timer handles, not render input.
  const phaseSimsRef = useRef(new Map());
  const revealTimeoutsRef = useRef(new Map());

  const installations = installationDocs || [];
  // Locked (request-scoped, R1): always pin to the resolved installation —
  // never left to auto-select or a free pick, and re-pinned if the caller
  // resolves a different installation. Unlocked: "detect from template" IS
  // the default regardless of installation count — including a single- or
  // zero-installation workspace (QA-flagged regression: auto-pinning to the
  // sole installation silently skipped inference entirely, so every upload
  // bound to it even when the template named a different site). The backend
  // infers/creates the installation from the template itself, so there is
  // never a reason to pre-empt that with an explicit pin here.
  useEffect(() => {
    if (lockedInstallation) {
      setInstallationId(String(lockedInstallation._id));
    }
  }, [lockedInstallation]);

  // Stop every live timer on unmount — both the phase simulations
  // (createUploadPhaseSimulation owns a real setInterval/setTimeout chain)
  // and the cosmetic reveal-hold timeouts.
  useEffect(
    () => () => {
      phaseSimsRef.current.forEach((sim) => sim.dispose());
      phaseSimsRef.current.clear();
      revealTimeoutsRef.current.forEach((id) => clearTimeout(id));
      revealTimeoutsRef.current.clear();
    },
    [],
  );

  // ---- sequential processing: one item in flight at a time ---------------
  useEffect(() => {
    if (processingRef.current) return;
    const next = nextQueueItemToProcess(items);
    if (!next || !installationId) return;
    processingRef.current = true;
    const itemId = next.id;
    dispatch({ type: 'START', id: itemId });

    const sim = createUploadPhaseSimulation({
      onChange: (snapshot) => setPhaseSnapshots((prev) => ({ ...prev, [itemId]: snapshot })),
      reducedMotion: prefersReducedMotion,
    });
    phaseSimsRef.current.set(itemId, sim);
    sim.start();

    // "Detect from template" is a FE-only sentinel — the request itself
    // omits installationId entirely so the backend infers it.
    const resolvedInstallationId =
      installationId === DETECT_INSTALLATION_VALUE ? undefined : installationId;

    createUpload
      .mutateAsync({
        file: next.file,
        installationId: resolvedInstallationId,
        reportingPeriod: next.reportingPeriod || undefined,
      })
      .then((result) => {
        // Clear the guard BEFORE dispatching: the dispatch's re-render can
        // run this effect again as part of the same update, so the ref
        // must already read `false` by then — clearing it in a trailing
        // `.finally()` instead would race React's flush and could leave
        // the queue permanently stuck after the very first item.
        processingRef.current = false;
        phaseSimsRef.current.get(itemId)?.complete();
        dispatch({ type: 'SUCCESS', id: itemId, result });
        // The reveal-hold is purely cosmetic — it never gates this
        // dispatch, so a fast response is never artificially slowed down
        // and the next queued file starts immediately underneath it.
        setRevealingIds((prev) => new Set(prev).add(itemId));
        const holdId = setTimeout(() => {
          setRevealingIds((prev) => {
            if (!prev.has(itemId)) return prev;
            const next2 = new Set(prev);
            next2.delete(itemId);
            return next2;
          });
          phaseSimsRef.current.delete(itemId);
          revealTimeoutsRef.current.delete(itemId);
        }, REVEAL_HOLD_MS);
        revealTimeoutsRef.current.set(itemId, holdId);
      })
      .catch((error) => {
        processingRef.current = false;
        phaseSimsRef.current.get(itemId)?.freeze();
        phaseSimsRef.current.delete(itemId);
        dispatch({
          type: 'FAILURE',
          id: itemId,
          error: {
            code: error?.payload?.error?.code,
            message: error?.payload?.error?.message,
            issues: error?.payload?.error?.issues,
          },
        });
      });
    // createUpload is intentionally excluded — this effect only needs to
    // react to the queue and the resolved installation; re-running it
    // because the mutation object's identity changed would risk starting an
    // item twice were it not for the processingRef guard, so it's kept out
    // of the dependency list entirely (same escape hatch already used in
    // SupplierEmissionsModal.jsx / RequestDirectEntryPage.jsx).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, installationId, prefersReducedMotion]);

  // ---- hand the settled batch to the caller, once -------------------------
  useEffect(() => {
    if (firedCompleteRef.current) return;
    if (isUploadQueueReadyToComplete(items)) {
      firedCompleteRef.current = true;
      onComplete?.(items);
    }
  }, [items, onComplete]);

  const resetQueue = () => {
    dispatch({ type: 'RESET' });
    firedCompleteRef.current = false;
    phaseSimsRef.current.forEach((sim) => sim.dispose());
    phaseSimsRef.current.clear();
    revealTimeoutsRef.current.forEach((id) => clearTimeout(id));
    revealTimeoutsRef.current.clear();
    setPhaseSnapshots({});
    setRevealingIds(new Set());
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    dispatch({ type: 'ADD_FILES', files });
  };

  const openPicker = () => fileInputRef.current?.click();

  // Note: there is deliberately no "add an installation first" block here
  // anymore (QA fix). A zero-installation, unlocked workspace is no longer
  // stopped from uploading — "detect from template" creates the installation
  // from the template itself, same as it does when it finds no name match
  // among existing ones. `lockedInstallation` truthy with zero installations
  // is provably unreachable: resolveRequestLineInstallationState only ever
  // resolves `matched` (which produces lockedInstallation) against a
  // non-empty installationDocs list.

  // ---- Batch in flight / settled: the per-file queue ---------------------
  if (items.length > 0) {
    const allSettled = isUploadQueueSettled(items);
    const anySuccess = uploadQueueHasSuccess(items);
    const processedCount = items.filter((it) => it.status !== UploadQueueItemStatus.Queued).length;
    // A "detect from template" file that found nothing to attach to can
    // only be fixed by picking explicitly — surface the SAME picker used
    // pre-drop right here, right when it's actually needed, rather than
    // cluttering every batch view with it unconditionally. Needs at least
    // one EXPLICIT alternative to offer (installations.length > 0) — with
    // zero installations there's nothing to pick between yet.
    const needsExplicitInstallationPick =
      !lockedInstallation &&
      installations.length > 0 &&
      items.some(
        (it) =>
          it.status === UploadQueueItemStatus.Failed &&
          isSupplierUploadMissingInstallationCode(it.serverError?.code),
      );

    return (
      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden mx-auto max-w-3xl w-full">
        <div className="flex items-center justify-between gap-3 px-6 py-3 border-b border-slate-100 bg-slate-50/60">
          <p className="text-xs font-semibold text-slate-500 m-0 uppercase tracking-wide">
            {t('commsTemplateDropzone.fileCount', {
              count: items.length,
              defaultValue: '{{count}} file',
              defaultValue_other: '{{count}} files',
            })}
          </p>
          {!allSettled && (
            <p className="text-xs text-slate-400 m-0">
              {t('commsTemplateDropzone.processedCount', '{{processed}}/{{total}} processed', {
                processed: processedCount,
                total: items.length,
              })}
            </p>
          )}
        </div>

        {needsExplicitInstallationPick && (
          <div className="px-6 py-3 border-b border-slate-100 bg-amber-50/60 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <span className="text-xs text-amber-700 shrink-0">
              {t(
                'commsTemplateDropzone.chooseInstallationForRetry',
                'Choose an installation for retry:',
              )}
            </span>
            <div className="max-w-xs w-full">
              <SelectField
                id="commsTemplateInstallationRetry"
                field={{
                  name: 'installationId',
                  value: installationId,
                  onChange: setInstallationId,
                  onBlur: () => {},
                }}
                options={buildInstallationOptions(t, installations)}
                placeholder={t(
                  'commsTemplateDropzone.installationPlaceholder',
                  'Which installation is this for?',
                )}
              />
            </div>
          </div>
        )}

        <ul className="list-none m-0 p-0 divide-y divide-slate-100">
          {items.map((item) => {
            const isRevealingComplete =
              item.status === UploadQueueItemStatus.Done && revealingIds.has(item.id);
            const showPhaseProgress =
              item.status === UploadQueueItemStatus.Uploading || isRevealingComplete;
            const resolvedInstallation = item.result?.upload?.resolvedInstallation;
            const failureCopy =
              item.status === UploadQueueItemStatus.Failed
                ? item.clientError ||
                  // lockedInstallation (R1) has no picker to send the supplier
                  // to — the nudge copy only applies where "the picker is
                  // right there" is actually true.
                  (!lockedInstallation &&
                  isSupplierUploadMissingInstallationCode(item.serverError?.code)
                    ? SUPPLIER_UPLOAD_MISSING_INSTALLATION_COPY
                    : supplierUploadRejectionCopy(item.serverError?.code, item.serverError?.message)
                        .title)
                : null;
            return (
              <li
                key={item.id}
                className="px-6 py-3.5"
              >
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon
                    icon={faFileExcel}
                    className="text-emerald-600 shrink-0"
                  />
                  <span className="text-sm font-medium text-slate-700 truncate flex-1">
                    {item.file.name}
                  </span>
                  {item.status === UploadQueueItemStatus.Queued && (
                    <span className="text-xs text-slate-400 inline-flex items-center gap-1.5 shrink-0">
                      <FontAwesomeIcon
                        icon={faCircle}
                        className="text-slate-200"
                      />
                      {t('commsTemplateDropzone.queuedStatus', 'Queued')}
                    </span>
                  )}
                  {showPhaseProgress && (
                    <span className="text-xs text-primaryColor font-medium inline-flex items-center gap-1.5 shrink-0">
                      <FontAwesomeIcon
                        icon={isRevealingComplete ? faCheckCircle : faSpinner}
                        className={isRevealingComplete ? '' : 'animate-spin'}
                      />
                      {Math.round(phaseSnapshots[item.id]?.percent ?? 0)}%
                    </span>
                  )}
                  {item.status === UploadQueueItemStatus.Done && !isRevealingComplete && (
                    <span className="text-xs text-emerald-600 font-semibold inline-flex items-center gap-1.5 shrink-0">
                      <FontAwesomeIcon icon={faCheckCircle} />
                      {t('commsTemplateDropzone.draftsReadyCount', {
                        count: item.result.drafts.length,
                        defaultValue: '{{count}} draft ready',
                        defaultValue_other: '{{count}} drafts ready',
                      })}
                    </span>
                  )}
                  {/* Held for its period, not failed: entering it sends the file again. */}
                  {isUploadQueueItemAwaitingPeriod(item) && (
                    <span className="text-xs text-sky-700 font-semibold inline-flex items-center gap-1.5 shrink-0">
                      <FontAwesomeIcon icon={faCalendarDays} />
                      {t('commsTemplateDropzone.needsPeriodStatus', 'Needs a reporting period')}
                    </span>
                  )}
                  {item.status === UploadQueueItemStatus.Failed &&
                    !isUploadQueueItemAwaitingPeriod(item) && (
                      <span className="text-xs text-red-600 font-semibold inline-flex items-center gap-1.5 shrink-0">
                        <FontAwesomeIcon icon={faTriangleExclamation} />
                        {t('commsTemplateDropzone.failedStatus', 'Failed')}
                      </span>
                    )}
                </div>

                {showPhaseProgress && (
                  <UploadPhaseProgress
                    snapshot={phaseSnapshots[item.id]}
                    complete={isRevealingComplete}
                  />
                )}

                {item.status === UploadQueueItemStatus.Done &&
                  !isRevealingComplete &&
                  resolvedInstallation && (
                    <p className="text-[11px] text-slate-400 mt-1.5 mb-0 ml-7 flex items-center gap-1.5">
                      <FontAwesomeIcon
                        icon={faIndustry}
                        className="text-[10px]"
                      />
                      {resolvedInstallation.name}
                      {resolvedInstallation.resolution === 'created' && (
                        <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-semibold px-1.5 py-0.5">
                          {t('commsTemplateDropzone.newInstallationBadge', 'New installation')}
                        </span>
                      )}
                    </p>
                  )}

                {item.status === UploadQueueItemStatus.Done &&
                  !isRevealingComplete &&
                  item.result?.warnings?.length > 0 && (
                    <TemplateIssuesPanel
                      parsedData={{ issues: item.result.warnings }}
                      className="mt-2 ml-7"
                    />
                  )}

                {isUploadQueueItemAwaitingPeriod(item) && (
                  <ReportingPeriodPrompt
                    parsedData={{
                      issues: item.serverError.issues?.length
                        ? item.serverError.issues
                        : [SUPPLIER_UPLOAD_PERIOD_ISSUE_FALLBACK],
                    }}
                    onApply={(reportingPeriod) =>
                      dispatch({ type: 'RETRY', id: item.id, reportingPeriod })
                    }
                    className="mt-2 ml-7"
                  />
                )}

                {item.status === UploadQueueItemStatus.Failed &&
                  !isSupplierUploadPeriodRequiredCode(item.serverError?.code) &&
                  item.serverError?.issues?.length > 0 && (
                    <TemplateIssuesPanel
                      parsedData={{ issues: item.serverError.issues }}
                      outcome="refused"
                      className="mt-2 ml-7"
                    />
                  )}

                {item.status === UploadQueueItemStatus.Failed && (
                  <div className="mt-1.5 ml-7 flex items-center justify-between gap-3">
                    {/* The period prompt above already says what is needed. */}
                    <p className="text-[11px] text-red-600/80 m-0">
                      {isUploadQueueItemAwaitingPeriod(item) ? null : failureCopy}
                    </p>
                    {/* Entering the period IS the retry for this rejection;
                        re-sending the file unchanged would only be rejected again.
                        The other way forward is to leave this file out — offered
                        only when another file made it, or there is nothing to go on to. */}
                    {anySuccess && isUploadQueueItemAwaitingPeriod(item) && (
                      <CgRadixButton
                        variant="ghost"
                        size="sm"
                        className="shrink-0"
                        onClick={() => dispatch({ type: 'DISMISS', id: item.id })}
                      >
                        {t(
                          'commsTemplateDropzone.continueWithoutFile',
                          'Continue without this file',
                        )}
                      </CgRadixButton>
                    )}
                    {!isSupplierUploadPeriodRequiredCode(item.serverError?.code) &&
                      !isSupplierUploadWorkbookFixCode(item.serverError?.code) && (
                        <CgRadixButton
                          variant="outline"
                          size="sm"
                          icon={faRotateLeft}
                          className="bg-white shrink-0"
                          onClick={() => dispatch({ type: 'RETRY', id: item.id })}
                        >
                          {t('commsTemplateDropzone.retryButton', 'Retry')}
                        </CgRadixButton>
                      )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        {allSettled && !anySuccess && (
          <div className="px-6 py-4 border-t border-slate-100">
            <CgRadixButton
              variant="ghost"
              icon={faRotateLeft}
              onClick={resetQueue}
            >
              {items.every((it) => isSupplierUploadWorkbookFixCode(it.serverError?.code))
                ? t('commsTemplateDropzone.uploadCorrectedButton', 'Upload a corrected file')
                : t('commsTemplateDropzone.tryAgainButton', 'Try again')}
            </CgRadixButton>
          </div>
        )}
      </div>
    );
  }

  // ---- Idle: installation target + accessible dropzone -------------------
  return (
    <div className="flex flex-col gap-3 mx-auto max-w-2xl w-full">
      {lockedInstallation ? (
        // R1: request-scoped upload — the target installation is resolved
        // from the request, shown as read-only context, never an open picker
        // (a multi-site supplier can't accidentally re-point the upload).
        <div className="max-w-xs rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 flex items-center gap-2 text-xs text-slate-600">
          <FontAwesomeIcon
            icon={faLock}
            className="text-slate-400"
          />
          <span>
            {t('commsTemplateDropzone.uploadingForPrefix', 'Uploading for')}{' '}
            <span className="font-semibold text-slate-700">{lockedInstallation.name}</span>
          </span>
        </div>
      ) : installations.length > 0 ? (
        // QA fix: the picker is visible from a SINGLE installation upward —
        // "Detect from template" defaults, but the explicit alternative(s)
        // stay one click away. With zero installations there's nothing to
        // pick between yet (detect is implicit, no picker rendered).
        // Item 19: labeled "Target installation" + an info popover — this is
        // WHERE the produced drafts get linked, not just a filter/context
        // field.
        <div className="max-w-xs">
          <InfoLabel
            htmlFor="commsTemplateInstallation"
            helpTitle={t('commsTemplateDropzone.targetInstallationLabel', 'Target installation')}
            help={t(
              'commsTemplateDropzone.targetInstallationHelp',
              'Values from this template are saved as emission drafts linked to this installation. Pick one explicitly, or choose “Detect from template” to match the installation named in the file (new ones are created automatically).',
            )}
            triggerTestId="targetInstallationHelp"
          >
            {t('commsTemplateDropzone.targetInstallationLabel', 'Target installation')}
          </InfoLabel>
          <SelectField
            id="commsTemplateInstallation"
            field={{
              name: 'installationId',
              value: installationId,
              onChange: setInstallationId,
              onBlur: () => {},
            }}
            options={buildInstallationOptions(t, installations)}
            placeholder={t(
              'commsTemplateDropzone.installationPlaceholder',
              'Which installation is this for?',
            )}
          />
        </div>
      ) : null}

      <div
        role="button"
        tabIndex={0}
        aria-label={t(
          'commsTemplateDropzone.uploadAriaLabel',
          'Upload your communication templates. Press Enter or Space to browse files, or drag and drop .xlsx files here.',
        )}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
        onClick={openPicker}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openPicker();
          }
        }}
        className={`rounded-2xl border-2 border-dashed text-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/50 focus-visible:ring-offset-2 p-12 ${
          isDragging
            ? 'border-primaryColor bg-primaryColor/5 cursor-pointer'
            : 'border-slate-300 bg-white hover:border-primaryColor/50 hover:bg-slate-50 cursor-pointer'
        }`}
      >
        <label
          htmlFor="commsTemplateFile"
          className="sr-only"
        >
          {t(
            'commsTemplateDropzone.fileInputLabel',
            'Communication template files (.xlsx, max 14MB each)',
          )}
        </label>
        <input
          ref={fileInputRef}
          id="commsTemplateFile"
          type="file"
          accept=".xlsx"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
        <div className="rounded-2xl bg-primaryColor/10 flex items-center justify-center mx-auto w-14 h-14 mb-4">
          <FontAwesomeIcon
            icon={faCloudArrowUp}
            className="text-primaryColor text-xl"
          />
        </div>
        <p className="font-semibold text-slate-700 m-0 text-sm">
          {t('commsTemplateDropzone.dragDropHeading', 'Drag & drop your communication templates')}
        </p>
        <p className="text-slate-400 mt-1 m-0 text-xs">
          {t(
            'commsTemplateDropzone.fileRequirementsNote',
            'EU Communication or EFDA template · .xlsx · max 14MB each · drop multiple at once',
          )}
        </p>
        <span className="inline-flex items-center gap-1.5 mt-4 text-xs font-semibold text-primaryColor">
          <FontAwesomeIcon icon={faFileExcel} />
          {t('commsTemplateDropzone.browseFilesLink', 'or browse files')}
        </span>
      </div>
    </div>
  );
}
